import React, { useState, useEffect, useCallback } from 'react';
import { statisticsApi } from './statisticsApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';

const month_ukr = [
  'січень', 'лютий', 'березень', 'квітень', 'травень', 'червень', 
  'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'
];

const day_ua = [
  { t: 'нд', col: 'red' },
  { t: 'пн' },
  { t: 'вт' },
  { t: 'ср' },
  { t: 'чт' },
  { t: 'пт' },
  { t: 'сб', col: 'red' }
];

const status_tabel = {
  'current': 'Не закритий',
  'closed': 'Закритий', 
  'need_approve': 'Потребує затвердження'
};

const StatisticaAttendanceGroup = ({ kgId, group, periodStartYear, isArchive }) => {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [attendanceInfo, setAttendanceInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Генерация периодов для выбора
  const generatePeriods = useCallback(() => {
    const MAX_LENGTH_PERIOD = 13;
    const MONTH_FINISH_PERIOD = 7;

    const curDate = new Date();
    const [curMonth, curYear] = [curDate.getMonth(), curDate.getFullYear()];

    let lengthPeriod;
    if (curYear > periodStartYear && curMonth > MONTH_FINISH_PERIOD) {
      lengthPeriod = MAX_LENGTH_PERIOD;
    } else if (curYear === periodStartYear && curMonth === 7) {
      lengthPeriod = 1;
    } else if (curMonth > MONTH_FINISH_PERIOD) {
      lengthPeriod = curMonth - (MONTH_FINISH_PERIOD - 1);
    } else {
      lengthPeriod = curMonth + (MONTH_FINISH_PERIOD - 1);
    }

    if (lengthPeriod < 0) {
      return [];
    }

    const iterCount = Math.min(lengthPeriod, MAX_LENGTH_PERIOD);
    const result = [];

    for (let iter = iterCount; iter > 0; iter--) {
      const y_iter = iter > 5 ? periodStartYear + 1 : periodStartYear;
      const m_iter_index = iter > 5 ? iter - 6 : iter + 6;
      result.push(`${m_iter_index + 1}.${y_iter}`);
    }

    return result;
  }, [periodStartYear]);

  // Инициализация периодов
  useEffect(() => {
    const generatedPeriods = generatePeriods();
    setPeriods(generatedPeriods);
    if (generatedPeriods.length > 0) {
      setSelectedPeriod(generatedPeriods[0]);
    }
  }, [generatePeriods]);

  // Обработка данных детей группы
  const processChildrenData = useCallback((attendanceData, childrenData) => {
    const childrenWithNumbers = childrenData.map(child => {
      const childInGroup = group.children?.find(c => c.childId === child._id);
      
      return {
        ...child,
        smallNumber: childInGroup?.smallNumber || 'недоступне',
        number: childInGroup?.number || 'недоступне'
      };
    });

    const result = {
      group: {
        kg_id: attendanceData[0]?._id?.idKg,
        gr_number: attendanceData[0]?._id?.grNb,
        month: attendanceData[0]?._id?.month
      },
      status: attendanceData[0]?._id?.status,
      tabel: []
    };

    attendanceData.forEach(child => {
      const childInfo = childrenWithNumbers.find(c => c._id === child._id?.idChild);
      
      const arrDays = child.tabel.main.map((el, i) => 
        (el === 1 || child.tabel.first[i] === 1) ? 1 : -1
      );

      result.tabel.push({
        child: {
          firstName: child.childInfo.firstName[0],
          lastName: child.childInfo.lastName[0],
          smallNumber: childInfo?.smallNumber || 'недоступне',
          big_number: childInfo?.number || 'недоступне',
          id: child._id.idChild,
          tPl: child._id.tPl
        },
        perdays: arrDays,
        perdaysfirst: child.tabel.first
      });
    });

    // Сортировка по фамилии
    result.tabel.sort((a, b) => 
      a.child.lastName.localeCompare(b.child.lastName)
    );

    return result;
  }, [group]);

  // Загрузка данных о посещаемости
  const loadAttendanceData = useCallback(async () => {
    if (!selectedPeriod) return;

    setLoading(true);
    try {
      // Основные данные о посещаемости
      const mainData = await statisticsApi.getGroupAttendance(kgId, group.number, selectedPeriod, isArchive);
      
      if (mainData.data && Array.isArray(mainData.data)) {
        // Временные данные
        const tempData = await statisticsApi.getGroupAttendanceTemp(kgId, group.number, selectedPeriod, isArchive);
        
        const combinedData = [
          ...mainData.data,
          ...(tempData.data || [])
        ];

        if (combinedData.length > 0) {
          // Данные о детях
          const childrenData = await statisticsApi.getGroupChildren(kgId, group._id, selectedPeriod);
          
          const processedData = processChildrenData(combinedData, childrenData.data || []);
          setAttendanceInfo(processedData);
        } else {
          setAttendanceInfo({ group: { gr_number: group.number } });
        }
      } else {
        setAttendanceInfo({ group: { gr_number: group.number } });
      }
    } catch (error) {
      console.error('Error loading attendance data:', error);
      setAttendanceInfo({ group: { gr_number: group.number } });
    } finally {
      setLoading(false);
    }
  }, [kgId, group, selectedPeriod, processChildrenData]);

  // Загрузка данных при изменении периода
  useEffect(() => {
    if (selectedPeriod) {
      loadAttendanceData();
    }
  }, [selectedPeriod, loadAttendanceData]);

  // Функция сортировки
  const handleSort = (key) => {
    if (!attendanceInfo?.tabel) return;
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Получение иконки сортировки
  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  // Отсортированные данные
  const sortedData = React.useMemo(() => {
    if (!attendanceInfo?.tabel || !sortConfig.key) return attendanceInfo?.tabel || [];
    
    return [...attendanceInfo.tabel].sort((a, b) => {
      const aValue = a.child[sortConfig.key];
      const bValue = b.child[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [attendanceInfo, sortConfig]);

  const handlePeriodChange = (e) => {
    setSelectedPeriod(e.target.value);
  };

  // Компонент для отображения отсутствия данных
  const NoDataInfo = () => (
    <div className="row">
      <div className="col-12 text-center py-4">
        <span className="text-muted">Статус: <b>Не має інформації за обраний період</b></span>
      </div>
    </div>
  );

  // Компонент таблицы с данными
  const AttendanceTable = () => {
    if (!attendanceInfo?.tabel) return <NoDataInfo />;

    const [month, year] = attendanceInfo.group.month.split('.');
    const daysInMonth = new Date(year, month, 0).getDate();

    const getDayInfo = (dayIndex) => {
      const day = new Date(year, month - 1, dayIndex + 1).getDay();
      return day_ua[day];
    };

    // Конфигурация колонок для сортировки
    const sortableColumns = [
      { key: 'smallNumber', label: 'Таб. №', width: '80px', sortable: true },
      { key: 'lastName', label: 'Прізвище', width: '150px', sortable: true },
      { key: 'firstName', label: "Ім'я", width: '120px', sortable: true },
    ];

    // Заголовки таблицы
    const renderTableHeaders = () => {
      return (
        <tr>
          {/* Сортируемые колонки */}
          {sortableColumns.map((column, idx) => (
            <th 
              key={column.key}
              style={{ 
                width: column.width,
                cursor: column.sortable ? 'pointer' : 'default',
                position: 'sticky',
                left: idx === 0 ? 0 : idx === 1 ? '80px' : '200px',
                backgroundColor: 'white',
                zIndex: 10,
                verticalAlign: 'middle'
              }}
              onClick={() => column.sortable && handleSort(column.key)}
              className="sortable-header"
            >
              <div className="d-flex align-items-center justify-content-between">
                {column.label}
                {column.sortable && (
                  <FontAwesomeIcon 
                    icon={getSortIcon(column.key)} 
                    className="ms-1" 
                    style={{ fontSize: '12px', opacity: sortConfig.key === column.key ? 1 : 0.3 }}
                  />
                )}
              </div>
            </th>
          ))}
          
          {/* Колонка "Відвідано" */}
          <th 
            style={{ 
              width: '80px',
              position: 'sticky',
              left: '300px',
              backgroundColor: 'white',
              zIndex: 10,
              verticalAlign: 'middle'
            }}
          >
            <b>Відвідано</b>
          </th>

          {/* Колонки дней месяца */}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const dayInfo = getDayInfo(i);
            return (
              <th 
                key={i} 
                style={{ 
                  width: '40px',
                  minWidth: '40px',
                  fontSize: '11px',
                  textAlign: 'center',
                  padding: '4px 2px',
                  verticalAlign: 'middle'
                }}
              >
                <div style={dayInfo.col ? { color: dayInfo.col } : {}}>
                  <div style={{ fontWeight: 'bold' }}>{i + 1}</div>
                  <div style={{ fontSize: '10px' }}>{dayInfo.t}</div>
                </div>
              </th>
            );
          })}
        </tr>
      );
    };

    // Строки с данными детей
    const renderChildRows = () => {
      const style_wobed = { 
        background: '#ffc632', 
        borderRadius: '2px', 
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: '30px'
      };
      const style_stnd = { 
        background: "#d4f7d4", 
        borderRadius: '2px', 
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: '30px'
      };

      return sortedData.map((child, index) => {
        const daysPresent = child.perdays.reduce((sum, day) => day === 1 ? sum + 1 : sum, 0);

        return (
          <tr key={child.child.id} style={{ borderBottom: '1px solid #e1dcdc'}}>
            {/* Сортируемые колонки */}
            <td 
              style={{ 
                position: 'sticky',
                left: 0,
                backgroundColor: 'white',
                verticalAlign: 'middle'
              }}
            >
              {child.child.smallNumber}
            </td>
            <td 
              style={{ 
                position: 'sticky',
                left: '80px',
                backgroundColor: 'white',
                verticalAlign: 'middle'
              }}
            >
              {child.child.lastName}
            </td>
            <td 
              style={{ 
                position: 'sticky',
                left: '200px',
                backgroundColor: 'white',
                verticalAlign: 'middle'
              }}
            >
              {child.child.firstName}
            </td>
            
            {/* Колонка "Відвідано" */}
            <td 
              style={{ 
                position: 'sticky',
                left: '300px',
                backgroundColor: 'white',
                fontWeight: 'bold',
                textAlign: 'center',
                verticalAlign: 'middle'
              }}
            >
              {daysPresent}
            </td>

            {/* Ячейки дней */}
            {child.perdays.slice(0, daysInMonth).map((day, dayIndex) => {
              const isPresent = day === 1;
              const dayInfo = getDayInfo(dayIndex);
              
              let style = {};
              if (isPresent && child.child.tPl === 'without-bed') {
                style = style_wobed;
              } else if (isPresent) {
                style = style_stnd;
              } else if (dayInfo.col) {
                style = { 
                  color: dayInfo.col, 
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: '30px'
                };
              } else {
                style = { 
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: '30px'
                };
              }

              return (
                <td key={dayIndex} style={{ padding: '2px', verticalAlign: 'middle' }}>
                  <div style={style}>
                    {isPresent ? 'п' : (day === -1 ? 'в' : '')}
                  </div>
                </td>
              );
            })}
          </tr>
        );
      });
    };

    // Итоговая строка
    const renderSummaryRow = () => {
      const daySums = Array.from({ length: daysInMonth }, (_, dayIndex) =>
        sortedData.reduce((sum, child) => 
          child.perdays[dayIndex] === 1 ? sum + 1 : sum, 0
        )
      );

      const totalSum = daySums.reduce((sum, daySum) => sum + daySum, 0);

      return (
        <tr style={{ fontWeight: 'bold',
              paddingBottom: '15px'  }}>
          <td 
            colSpan="3"
            style={{ 
              position: 'sticky',
              left: 0,
              backgroundColor: 'white',
              verticalAlign: 'middle'
            }}
          >
            Всього:
          </td>
          <td 
            style={{ 
              position: 'sticky',
              left: '300px',
              backgroundColor: 'white',
              textAlign: 'center',
              verticalAlign: 'middle'
            }}
          >
            {totalSum}
          </td>
          {daySums.map((sum, index) => {
            const dayInfo = getDayInfo(index);
            return (
              <td 
                key={index} 
                style={{ 
                  textAlign: 'center',
                  padding: '2px',
                  verticalAlign: 'middle',
                  color: dayInfo.col || 'inherit'
                }}
              >
                {sum}
              </td>
            );
          })}
        </tr>
      );
    };

    return (
      <div className="">
        {/* Таблица с горизонтальной прокруткой */}
        <div className="table-responsive" style={{ 
          overflowX: 'auto',
          //border: '1px solid #dee2e6',
          borderRadius: '8px',
          maxHeight: '70vh',
          //padding: '10px'
        }}>
          <table className="table table-sm mb-0" style={{ border: 'none' }}>
            <thead style={{ 
              position: 'sticky', 
              top: 0, 
              backgroundColor: 'white',
              zIndex: 20,
              borderBottom: '2px solid black' 
            }}>
              {renderTableHeaders()}
            </thead>
            <tbody>
              {renderChildRows()}
            </tbody>
            <tfoot style={{ 
              position: 'sticky', 
              bottom: 0, 
              backgroundColor: 'white',
              zIndex: 15
            }}>
              {renderSummaryRow()}
            </tfoot>
          </table>
        </div>

        {/* Легенда */}
        <div className="row mt-3">
          <div className="col-12">
            <div className="d-flex align-items-center flex-wrap gap-3">
              <small className="text-muted">Кольорами позначені типи місць:</small>
              <div className="d-flex align-items-center gap-1">
                <span style={{ 
                  display: 'inline-block', 
                  width: '14px', 
                  height: '14px', 
                  background: '#ffc632',
                  borderRadius: '2px'
                }}></span>
                <small>короткотривале</small>
              </div>
              <div className="d-flex align-items-center gap-1">
                <span style={{ 
                  display: 'inline-block', 
                  width: '14px', 
                  height: '14px', 
                  background: '#d4f7d4',
                  borderRadius: '2px'
                }}></span>
                <small>повного дня</small>
              </div>
              <div className="d-flex align-items-center gap-1">
                <small className="text-muted">п - присутній, в - відсутній</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid py-3">
      <div className="row">
        <div className="col-12">
          {/* Панель выбора периода */}
          <div className=" mb-4">
            <div className="d-flex align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label fw-semibold mb-0">Оберіть період:</label>
                <select 
                  className="form-select"
                  onChange={handlePeriodChange} 
                  value={selectedPeriod}
                  disabled={loading || periods.length === 0}
                  style={{ width: 'auto', minWidth: '200px' }}
                >
                  {periods.map((period, index) => (
                    <option key={index} value={period}>
                      {`${month_ukr[+period.split('.')[0] - 1]} ${period.split('.')[1]}`}
                    </option>
                  ))}
                </select>
              </div>
              
              {loading && (
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  <span>Завантаження даних...</span>
                </div>
              )}
            </div>
          </div>

          {/* Основное содержимое */}
          {attendanceInfo?.tabel ? <AttendanceTable /> : <NoDataInfo />}
        </div>
      </div>

      <style jsx>{`
        .sortable-header:hover {
          background-color: #f8f9fa !important;
        }
        
        .table th.sortable-header {
          transition: background-color 0.15s ease;
        }
        
        .table-responsive::-webkit-scrollbar {
          height: 8px;
        }
        
        .table-responsive::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .table-responsive::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        .table-responsive::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        /* Убираем серые полосы и границы */
        .table > :not(caption) > * > * {
          border: none;
          background-color: transparent;
        }

        .table-striped > tbody > tr:nth-of-type(odd) > * {
          --bs-table-accent-bg: transparent;
        }

        .table-active {
          --bs-table-accent-bg: transparent;
        }
      `}</style>
    </div>
  );
};

export default StatisticaAttendanceGroup;